import { Injectable, Injector, ComponentRef, TemplateRef } from '@angular/core';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';

import { ModalRef } from './modal-ref';
import { MODAL_CONTENT_DATA } from './modal.tokens';
import { ModalContainer } from './modal-container.component';

export interface ModalContent {
  title?: string | TemplateRef<any>;
  body?: string | TemplateRef<any>;
  closeButton?: boolean;
  actions?: {
    label: string;
    type: 'primary' | 'secondary';
    tag?: string;
  }[];
}

export interface ModalConfig {
  closeOnBackdropClick?: boolean;
  panelClass?: string;
  hasBackdrop?: boolean;
  backdropClass?: string;
  content?: ModalContent;
}

const DEFAULT_CONFIG: ModalConfig = {
  closeOnBackdropClick: true,
  hasBackdrop: true,
  backdropClass: 'backdrop--dark',
  panelClass: 'modal--default',
  content: null
};

@Injectable()
export class ModalService {

  constructor(
    private injector: Injector,
    private overlay: Overlay
  ) { }

  open(config: ModalConfig = {}) {
    // Override default configuration
    const dialogConfig = { ...DEFAULT_CONFIG, ...config };

    // Returns an OverlayRef which is a PortalHost
    const overlayRef = this.createOverlay(dialogConfig);

    // Instantiate remote control
    const modalRef = new ModalRef(overlayRef);

    const overlayComponent = this.attachModalContainer(overlayRef, dialogConfig, modalRef);

    modalRef.componentInstance = overlayComponent;

    if (dialogConfig.closeOnBackdropClick) {
      overlayRef.backdropClick().subscribe(_ => modalRef.close('cancel'));
    }

    return modalRef;
  }

  private createOverlay(config: ModalConfig) {
    const overlayConfig = this.getOverlayConfig(config);
    return this.overlay.create(overlayConfig);
  }

  private attachModalContainer(overlayRef: OverlayRef, config: ModalConfig, modalRef: ModalRef) {
    const injector = this.createInjector(config, modalRef);

    const containerPortal = new ComponentPortal(ModalContainer, null, injector);
    const containerRef: ComponentRef<ModalContainer> = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private createInjector(config: ModalConfig, modalRef: ModalRef): PortalInjector {
    const injectionTokens = new WeakMap();

    injectionTokens.set(ModalRef, modalRef);
    injectionTokens.set(MODAL_CONTENT_DATA, config.content);

    return new PortalInjector(this.injector, injectionTokens);
  }

  private getOverlayConfig(config: ModalConfig): OverlayConfig {
    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .centerVertically();

    const overlayConfig = new OverlayConfig({
      hasBackdrop: config.hasBackdrop,
      backdropClass: config.backdropClass,
      panelClass: config.panelClass,
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      positionStrategy
    });

    return overlayConfig;
  }
}
